import { styled } from '@mui/material/styles';
import { alpha } from '@mui/system';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useLocales } from 'src/locales';
import { useDocContentContext } from '../DocContentContext';

const NavWrapper = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
  },
}));

const IndicatorContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(3),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  width: '32px',
  alignItems: 'flex-end',
  opacity: 1,
  pointerEvents: 'all',
  zIndex: 1002,
  cursor: 'pointer',
  padding: theme.spacing(1),
  margin: theme.spacing(-1),

  '&.isHovered': { opacity: 0.5 },
}));

const IndicatorLine = styled('div')(({ theme }) => ({
  height: '3px',
  backgroundColor: theme.palette.text.disabled,
  borderRadius: theme.spacing(0.5),
  width: '100%',

  '&.small': { width: '50%' },
  '&.active': { backgroundColor: theme.palette.primary.main },
  '&.last': { marginBottom: 0 },
}));

const NavContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(8),
  top: theme.spacing(6),
  width: '300px',
  maxHeight: '60vh',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  opacity: 0,
  visibility: 'hidden',
  transition: theme.transitions.create(['opacity', 'visibility'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  overflow: 'hidden',
  pointerEvents: 'all',
  zIndex: 1003,
  display: 'flex',
  flexDirection: 'column',

  '&.isHovered': {
    opacity: 1,
    visibility: 'visible',
  },
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
}));

const NavHeader = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const NavTitle = styled('div')(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.primary,
  fontWeight: 600,
}));

const NavList = styled('div')(() => ({
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
}));

const NavItem = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.75, 1.2),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(0.75),
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'color'], {
    duration: theme.transitions.duration.shorter,
  }),
  position: 'relative',

  '&.active': { backgroundColor: alpha(theme.palette.primary.light, 0.2) },
  '&:hover:not(.active)': { backgroundColor: theme.palette.action.hover },
}));

const NavItemText = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,

  // Limitar texto a 2 linhas
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  '&.active': {
    color: theme.palette.primary.main,
    textShadow: '0.5px 0 0 currentColor',
  },

  '&:hover': { color: theme.palette.text.primary },
}));

export const DocContentNav = () => {
  const { editor, tableOfContents } = useDocContentContext();
  const { translate } = useLocales();
  const [isHovered, setIsHovered] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);

  const handleMouseEnter = () => setIsHovered(true);

  const handleMouseLeave = () => setIsHovered(false);

  const handleNavItemClick = (headingId: string) => {
    editor.commands.scrollToHeading(headingId);
  };

  useEffect(() => {
    if (!tableOfContents.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectingHeadings = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => {
            const aTop = a.boundingClientRect.top;
            const bTop = b.boundingClientRect.top;
            return aTop - bTop;
          });

        const [activeHeading] = intersectingHeadings;
        if (activeHeading?.target?.id) {
          setActiveHeadingId(activeHeading.target.id);
        }
      },
      {
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0.1,
      },
    );

    tableOfContents.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [tableOfContents]);

  if (!tableOfContents || tableOfContents.length === 0) return null;

  return (
    <NavWrapper className="no-print">
      <IndicatorContainer
        className={classNames({ isHovered })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {tableOfContents.length > 0 &&
          tableOfContents.slice(0, 30).map((item, index) => (
            <IndicatorLine
              key={`indicator-line-${index}`}
              className={classNames({
                active: item.id === activeHeadingId,
                last: index === tableOfContents.length - 1,
                small: item.level > 1,
              })}
            />
          ))}
      </IndicatorContainer>

      <NavContainer
        className={classNames({ isHovered })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <ContentWrapper>
          <NavHeader>
            <NavTitle>{translate('shared.labels.contents')}</NavTitle>
          </NavHeader>

          <NavList>
            {tableOfContents.map((item, index) => (
              <NavItem
                key={`nav-item-${index}`}
                className={classNames({ active: item.id === activeHeadingId })}
                style={{ marginLeft: `${(item.level - 1) * 12}px` }}
                onClick={() => handleNavItemClick(item.id)}
              >
                <NavItemText
                  className={classNames({
                    active: item.id === activeHeadingId,
                  })}
                >
                  {item.textContent}
                </NavItemText>
              </NavItem>
            ))}
          </NavList>
        </ContentWrapper>
      </NavContainer>
    </NavWrapper>
  );
};
